import { useRef, useState, type MouseEvent } from 'react';
import type { CasePhoto } from '@/domain';
import { useAddKitPhoto, useCasePhotoUrl, useKitPhotos, useRemoveKitPhoto } from '@/application/usecases/useKits';
import { casePhotoItem } from '@/features/photos/photoViewerItems';
import { openPhotoTile } from '@/features/photos/photoViewerStore';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CloseIcon } from '@/ui/icons';
import styles from './EditCasePhotos.module.css';

/**
 * The photographs of a case, on the edit screen, where they can be added to and
 * taken off.
 *
 * Reception photographs what arrived in the box. Sometimes that is photographed
 * twice, sideways, or with a thumb over the impression, and until now the only
 * way to correct it was to leave the wrong picture on the case for ever: there
 * was an upload and no way back.
 *
 * They are NOT part of the form. Every other field on this screen is collected
 * and sent on Save as one patch; a photograph is a file and a file is its own
 * request, so each one takes effect as it is chosen or dropped. That is the
 * honest behaviour and it is stated on screen, rather than a Save button that
 * silently means two different things.
 *
 * Proof-of-work photographs are not here and cannot be removed anywhere: they
 * record that a stage was finished, and a record somebody can delete when it
 * becomes inconvenient is not a record.
 *
 * Taking one off asks first, in the shared confirmation, because nothing brings
 * it back. Adding one does not: choosing the file is the deliberate step. The
 * request of 5 October 2026 (third), section 2.
 */
export function EditCasePhotos({
  kitId,
  jobOrderNo,
}: {
  kitId: string;
  /**
   * The case, for the name of a photograph somebody downloads from the viewer
   * and for the question asked before one is removed.
   */
  jobOrderNo?: string;
}) {
  const photos = useKitPhotos(kitId);
  const add = useAddKitPhoto();
  const remove = useRemoveKitPhoto();
  const ask = useConfirmAction();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const items = photos.data ?? [];
  const viewed = items.map((photo) => casePhotoItem(photo));

  /** One request each, in order, so a slow connection fails one and not the set. */
  async function upload(files: File[]) {
    setBusy(true);
    for (const file of files) {
      try {
        await add.mutateAsync({ kitId, file });
      } catch (failure) {
        toast(failureWords(failure));
      }
    }
    setBusy(false);
  }

  return (
    <div className={styles.photos}>
      <span className={styles.l}>{strings.editCase.photos}</span>
      <p className={styles.note}>{strings.editCase.photosHint}</p>

      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        aria-label={strings.editCase.photosInputLabel}
        className={styles.fileInput}
        onChange={(event) => {
          const picked = [...(event.target.files ?? [])];
          // Cleared so picking the same file twice still fires a change.
          event.target.value = '';
          if (picked.length > 0) void upload(picked);
        }}
      />

      {photos.isPending ? (
        <p className={styles.note}>{strings.common.loadingCase}</p>
      ) : items.length === 0 ? (
        <p className={styles.none}>{strings.record.noPhotos}</p>
      ) : (
        <ul className={styles.strip} data-photo-tiles="">
          {items.map((photo, index) => (
            <li key={photo.id} className={styles.chip}>
              {/* Opens it in the viewer; removing it is the button beside this one, never inside it. */}
              <Thumb
                photo={photo}
                label={viewed[index]?.caption ?? ''}
                onOpen={(event) => openPhotoTile(event, viewed, index, jobOrderNo)}
              />
              <button
                type="button"
                className={styles.drop}
                disabled={remove.isPending}
                aria-label={`${strings.editCase.photosRemove} ${photo.id}`}
                onClick={() =>
                  ask(
                    strings.confirm.removeCasePhoto(jobOrderNo ?? null),
                    () =>
                      remove.mutateAsync({ kitId, photoId: photo.id }).then(() => {
                        toast(strings.editCase.photoRemoved);
                      }),
                    { confirmLabel: strings.editCase.photosRemove },
                  )
                }
              >
                <CloseIcon />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Button type="button" disabled={busy} onClick={() => input.current?.click()}>
        {busy
          ? strings.editCase.photosAdding
          : items.length === 0
            ? strings.editCase.photosAdd
            : strings.editCase.photosAddMore}
      </Button>
    </div>
  );
}

/**
 * The bytes come through the port, never an authenticated URL on an `<img>`: a
 * browser fetches an image without headers, which is what made every photograph
 * in the system a broken image the first time.
 *
 * A button, which opens the photograph in the image viewer: 72 pixels is not
 * enough to tell which of two pictures of a box is the sideways one before
 * taking it off the case. The request of 5 October 2026 (third), section 6.
 */
function Thumb({
  photo,
  label,
  onOpen,
}: {
  photo: CasePhoto;
  label: string;
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const objectUrl = useCasePhotoUrl(photo.thumbnailUrl);

  return (
    <button
      type="button"
      className={styles.open}
      aria-label={strings.viewer.open(`${strings.record.photos} · ${label}`)}
      onClick={onOpen}
      data-photo-tile={photo.id}
    >
      {objectUrl === null ? (
        <span className={styles.blank} aria-hidden="true" data-photo-frame="" />
      ) : (
        <img className={styles.img} src={objectUrl} alt="" draggable={false} data-photo-frame="" />
      )}
    </button>
  );
}
